import React, { useState } from 'react';
import { sampleData } from '../../../../../DummyData/ConvertSampleJson';

// Define types for our component
interface JsonToTypeScriptProps {}

// TypeScript theme options
type TypeScriptTheme = 'standard' | 'detailed' | 'minimal';

// TypeScript declaration options
type DeclarationType = 'interface' | 'type';

const JsonToTypeScript: React.FC<JsonToTypeScriptProps> = () => {
  // State for the JSON input
  const [jsonInput, setJsonInput] = useState<string>('');
  // State for the TypeScript output
  const [tsOutput, setTsOutput] = useState<string>('');
  // State for error messages
  const [error, setError] = useState<string>('');
  // State for the root interface name
  const [interfaceName, setInterfaceName] = useState<string>('RootObject');
  // State for loading indicator
  const [isLoading, setIsLoading] = useState<boolean>(false);
  // State for showing advanced options
  const [showAdvanced, setShowAdvanced] = useState<boolean>(false);
  // State to track if the conversion happened
  const [isTsGenerated, setIsTsGenerated] = useState<boolean>(false);
  // State for copied indicator
  const [copied, setCopied] = useState<boolean>(false);
  // State for options
  const [options, setOptions] = useState({
    useInterfaces: true,
    usePrettyIndent: true,
    useExport: true,
    useStrictNullChecks: false,
    generateJsDoc: true
  });


  // Handle JSON input change
  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setJsonInput(e.target.value);
    setError('');
    setIsTsGenerated(false);
  };

  // Handle interface name change
  const handleInterfaceNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInterfaceName(e.target.value);
  };

  // Use example data
  const useExampleData = () => {
    setJsonInput(JSON.stringify(sampleData, null, 2));
    setError('');
    setIsTsGenerated(false);
    setTsOutput('');
  };

  // Beautify/Format JSON
  const beautifyJson = () => {
    try {
      if (!jsonInput.trim()) {
        setError('Please enter JSON data to format');
        return;
      }
      
      const parsedJson = JSON.parse(jsonInput);
      const beautified = JSON.stringify(parsedJson, null, 2);
      setJsonInput(beautified);
      setError('');
    } catch (e) {
      setError(`Invalid JSON format: ${e instanceof Error ? e.message : String(e)}`);
    }
  };

  // Validate JSON
  const validateJson = (jsonString: string): boolean => {
    try {
      JSON.parse(jsonString);
      return true;
    } catch (e) {
      return false;
    }
  };

  // Clear all inputs and reset states
  const clearAll = () => {
    setJsonInput('');
    setTsOutput('');
    setError('');
    setInterfaceName('RootObject');
    setOptions({
      useInterfaces: true,
      usePrettyIndent: true,
      useExport: true,
      useStrictNullChecks: false,
      generateJsDoc: true
    });
    setIsTsGenerated(false);
  };

  // Toggle an option
  const toggleOption = (option: keyof typeof options) => {
    setOptions(prev => ({
      ...prev,
      [option]: !prev[option]
    }));
  };

  // Helper to capitalize first letter for interface names
  const capitalizeFirstLetter = (str: string): string => {
    if (!str) return 'Unknown';
    return str.charAt(0).toUpperCase() + str.slice(1);
  };

  // Generate TypeScript interfaces for JSON data
  const generateTypeScript = () => {
    setError('');
    setIsLoading(true);
    setIsTsGenerated(false);
    setTsOutput('');
    
    if (!jsonInput.trim()) {
      setError('Please enter JSON data');
      setIsLoading(false);
      return;
    }

    if (!validateJson(jsonInput)) {
      setError('Invalid JSON format. Please check your input.');
      setIsLoading(false);
      return;
    }

    try {
      // Parse JSON
      const jsonData = JSON.parse(jsonInput);
      
      // Generate TypeScript interfaces
      let result = '';
      
      // Add header comment if JSDoc is enabled
      if (options.generateJsDoc) {
        result += `/**\n * Generated TypeScript interfaces based on JSON structure\n */\n\n`;
      }
      
      // Track processed interfaces to avoid duplicates
      const processedInterfaces = new Map<string, boolean>();
      const interfaces: string[] = [];
      
      // Helper function to process objects recursively
      const processObject = (obj: Record<string, any>, name: string): void => {
        // Skip if already processed
        if (processedInterfaces.has(name)) return;
        processedInterfaces.set(name, true);
        
        const properties: string[] = [];
        
        // Process each property
        for (const [key, value] of Object.entries(obj)) {
          let type = '';
          
          // Handle null values
          if (value === null) {
            type = options.useStrictNullChecks ? 'null' : 'any';
          }
          // Handle arrays
          else if (Array.isArray(value)) {
            if (value.length === 0) {
              type = 'any[]';
            } else if (typeof value[0] === 'object' && value[0] !== null) {
              // Create interface for array items
              const itemName = `${capitalizeFirstLetter(key)}Item`;
              processObject(value[0] as Record<string, any>, itemName);
              type = `${itemName}[]`;
            } else {
              // Infer type from array items
              type = `${typeof value[0]}[]`;
            }
          }
          // Handle objects
          else if (typeof value === 'object') {
            const nestedName = capitalizeFirstLetter(key);
            processObject(value as Record<string, any>, nestedName);
            type = nestedName;
          }
          // Handle primitive types
          else {
            type = typeof value;
            // Special case for date strings
            if (type === 'string' && 
                (key.toLowerCase().includes('date') || key.toLowerCase().includes('time')) && 
                /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(value as string)) {
              type = 'Date';
            }
          }
          
          // Add property to the list with proper indentation
          properties.push(`  ${key}: ${type};`);
        }
        
        // Create interface string
        let interfaceStr = '';
        
        // Add JSDoc comment
        if (options.generateJsDoc) {
          interfaceStr += `/**\n * ${name} interface\n */\n`;
        }
        
        // Use interface or type alias based on options
        const declaration = options.useInterfaces ? 'interface' : 'type';
        const exportPrefix = options.useExport ? 'export ' : '';
        
        if (options.useInterfaces) {
          interfaceStr += `${exportPrefix}${declaration} ${name} {\n${properties.join('\n')}\n}\n\n`;
        } else {
          interfaceStr += `${exportPrefix}${declaration} ${name} = {\n${properties.join('\n')}\n};\n\n`;
        }
        
        interfaces.push(interfaceStr);
      };
      
      // Start processing from the root object
      processObject(jsonData as Record<string, any>, interfaceName);
      
      // Join all interfaces
      result += interfaces.join('');
      
      // Update output
      setTsOutput(result);
      setIsTsGenerated(true);
      setError('');
      setIsLoading(false);
    } catch (e) {
      setError(`Error generating TypeScript: ${e instanceof Error ? e.message : String(e)}`);
      setIsLoading(false);
    }
  };

  // Copy TypeScript to clipboard
  const copyToClipboard = () => {
    navigator.clipboard.writeText(tsOutput)
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      })
      .catch(() => {
        setError('Failed to copy to clipboard');
      });
  };

  // Download TypeScript file
  const downloadTypeScript = () => {
    const blob = new Blob([tsOutput], { type: 'text/typescript' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    
    link.href = url;
    link.download = `${interfaceName.toLowerCase() || 'types'}.ts`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="max-w-6xl mx-auto">
      <h1 className="text-2xl font-bold mb-6">JSON to TypeScript Converter</h1>
      
      {/* Description */}
      <div className="mb-6 bg-blue-50 p-4 rounded-md border border-blue-100">
        <p className="text-blue-800">
          Convert JSON data to TypeScript interfaces or types. Paste your JSON data below to generate well-typed TypeScript declarations.
        </p>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Input Section */}
        <div className="flex flex-col h-full">
          <div className="bg-white p-4 rounded-md shadow-sm mb-4 flex-grow">
            <div className="flex justify-between items-center mb-3">
              <label htmlFor="jsonInput" className="font-medium text-gray-700">
                JSON Input
              </label>
              <div className="flex space-x-2">
                <button
                  className="text-sm text-blue-600 hover:text-blue-800"
                  onClick={useExampleData}
                >
                  Use Example
                </button>
                <button
                  className="text-sm text-blue-600 hover:text-blue-800"
                  onClick={beautifyJson}
                >
                  Format JSON
                </button>
                <button
                  className="text-sm text-gray-600 hover:text-gray-800"
                  onClick={() => setJsonInput('')}
                >
                  Clear
                </button>
              </div>
            </div>
            <textarea
              id="jsonInput"
              className="w-full h-80 p-3 border border-gray-300 rounded-md font-mono text-sm focus:ring-blue-500 focus:border-blue-500"
              value={jsonInput}
              onChange={handleInputChange}
              placeholder='Paste your JSON data here, e.g., {"name": "John", "age": 30}'
            />
          </div>
          
          {/* Configuration */}
          <div className="bg-white p-4 rounded-md shadow-sm">
            <div className="mb-4">
              <label htmlFor="interfaceName" className="block text-sm font-medium text-gray-700 mb-1">
                Root Interface Name
              </label>
              <input
                type="text"
                id="interfaceName"
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                value={interfaceName}
                onChange={handleInterfaceNameChange}
                placeholder="Enter root interface name"
              />
            </div>
            
            <div className="mb-4">
              <button
                className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
                onClick={() => setShowAdvanced(!showAdvanced)}
              >
                <span>{showAdvanced ? 'Hide' : 'Show'} Advanced Options</span>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className={`h-4 w-4 ml-1 transition-transform ${showAdvanced ? 'transform rotate-180' : ''}`}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
            </div>
            
            {showAdvanced && (
              <div className="space-y-4 p-3 bg-gray-50 rounded-md mb-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="useInterfaces"
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      checked={options.useInterfaces}
                      onChange={() => toggleOption('useInterfaces')}
                    />
                    <label htmlFor="useInterfaces" className="ml-2 block text-sm text-gray-700">
                      Use Interfaces
                    </label>
                  </div>
                  
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="useExport"
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      checked={options.useExport}
                      onChange={() => toggleOption('useExport')}
                    />
                    <label htmlFor="useExport" className="ml-2 block text-sm text-gray-700">
                      Export Types
                    </label>
                  </div>
                  
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="usePrettyIndent"
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      checked={options.usePrettyIndent}
                      onChange={() => toggleOption('usePrettyIndent')}
                    />
                    <label htmlFor="usePrettyIndent" className="ml-2 block text-sm text-gray-700">
                      Pretty Indentation
                    </label>
                  </div>
                  
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="generateJsDoc"
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      checked={options.generateJsDoc}
                      onChange={() => toggleOption('generateJsDoc')}
                    />
                    <label htmlFor="generateJsDoc" className="ml-2 block text-sm text-gray-700">
                      Generate JSDoc
                    </label>
                  </div>
                  
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="useStrictNullChecks"
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      checked={options.useStrictNullChecks}
                      onChange={() => toggleOption('useStrictNullChecks')}
                    />
                    <label htmlFor="useStrictNullChecks" className="ml-2 block text-sm text-gray-700">
                      Strict Null Checks
                    </label>
                  </div>
                </div>
                
                <div>
                  <p className="text-xs text-gray-500 mt-1">
                    These options control how TypeScript types are generated. Use interfaces for extensibility, or types for exact matches.
                  </p>
                </div>
              </div>
            )}
            
            <div className="mt-4 flex justify-between">
              <button
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                onClick={generateTypeScript}
                disabled={isLoading}
              >
                {isLoading ? (
                  <div className="flex items-center">
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Processing...
                  </div>
                ) : (
                  'Generate TypeScript'
                )}
              </button>
              
              <button
                className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
                onClick={clearAll}
              >
                Clear All
              </button>
            </div>
          </div>
        </div>
        
        {/* Output Section */}
        <div className="bg-white p-4 rounded-md shadow-sm h-full flex flex-col">
          <div className="flex justify-between items-center mb-3">
            <label className="font-medium text-gray-700">
              TypeScript Output
            </label>
            
            {isTsGenerated && (
              <div className="flex space-x-2">
                <button
                  className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
                  onClick={copyToClipboard}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-4 mr-1"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                  {copied ? 'Copied!' : 'Copy TypeScript'}
                </button>
                
                <button
                  className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
                  onClick={downloadTypeScript}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-4 mr-1"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                  </svg>
                  Download
                </button>
              </div>
            )}
          </div>
          
          <div className="flex-grow flex flex-col">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 p-3 rounded-md mb-3">
                <p className="font-medium">Error</p>
                <p>{error}</p>
              </div>
            )}
            
            {!error && !isTsGenerated && (
              <div className="bg-gray-50 border border-gray-200 text-gray-500 p-4 rounded-md flex-grow flex items-center justify-center">
                <div className="text-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mx-auto text-gray-400 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                  </svg>
                  <p>
                    TypeScript definitions will appear here after conversion.
                  </p>
                </div>
              </div>
            )}
            
            {!error && isTsGenerated && (
              <div className="flex-grow flex flex-col">
                <div className="flex-grow">
                  <textarea
                    id="tsOutput"
                    className="w-full h-full p-3 border border-gray-300 rounded-md font-mono text-sm focus:ring-blue-500 focus:border-blue-500"
                    value={tsOutput}
                    readOnly
                  />
                </div>
              </div>
            )}
          </div>
          
          {isTsGenerated && (
            <div className="mt-4 bg-green-50 border border-green-200 text-green-700 p-3 rounded-md">
              <div className="flex items-center mb-2">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <p className="font-medium">TypeScript Successfully Generated!</p>
              </div>
              <p className="text-sm">
                The generated TypeScript interfaces can be used in your TypeScript projects for type-safe code.
              </p>
            </div>
          )}
        </div>
      </div>
      
      {/* Information Section */}
      <div className="mt-8 bg-white p-6 rounded-md shadow-sm">
        <h2 className="text-lg font-semibold mb-4">About JSON to TypeScript Conversion</h2>
        
        <div className="space-y-4">
          <div>
            <h3 className="font-medium text-gray-800 mb-2">How This Converter Works</h3>
            <p className="text-gray-600">
              This tool analyzes your JSON data structure and generates corresponding TypeScript interfaces or type aliases.
              It automatically detects nested objects, arrays, and infers appropriate types for your data.
            </p>
          </div>
          
          <div>
            <h3 className="font-medium text-gray-800 mb-2">Features</h3>
            <ul className="list-disc pl-5 text-gray-600">
              <li><strong>Smart Type Inference</strong> - Automatically detects types including dates</li>
              <li><strong>Nested Interface Generation</strong> - Creates separate interfaces for nested objects</li>
              <li><strong>Customization Options</strong> - Choose between interfaces or types, export declarations, and more</li>
              <li><strong>JSDoc Support</strong> - Generate documentation comments for better IDE integration</li>
              <li><strong>Copy & Download</strong> - Easily use the generated TypeScript in your projects</li>
            </ul>
          </div>
          
          <div>
            <h3 className="font-medium text-gray-800 mb-2">Tips for Better Results</h3>
            <ul className="list-disc pl-5 text-gray-600">
              <li>Use descriptive property names in your JSON for better interface naming</li>
              <li>Include array items in your sample JSON to ensure proper typing of arrays</li>
              <li>For date fields, use ISO format strings for automatic Date type detection</li>
              <li>Use the "Format JSON" button to validate your JSON before conversion</li>
              <li>Choose "Interfaces" for extendable types or "Type Aliases" for exact matching</li>
            </ul>
          </div>
          
          <div>
            <h3 className="font-medium text-gray-800 mb-2">Using the Generated TypeScript</h3>
            <p className="text-gray-600">
              Simply copy or download the generated TypeScript code and include it in your project. 
              You can then use these interfaces to type your variables, function parameters, and return types 
              for better type safety and code completion.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default JsonToTypeScript;